"use client"

import { useRef } from "react"
import Image from "next/image"
import { toDisplayImageUrl } from "@lib/util/encode-remote-image-url"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type HomeCategoryRailItemView = {
  id: string
  title: string
  href: string
  imageUrl: string | null
}

type HomeCategoryRailProps = {
  items: HomeCategoryRailItemView[]
}

export default function HomeCategoryRail({ items }: HomeCategoryRailProps) {
  const scrollerRef = useRef<HTMLUListElement>(null)

  if (!items.length) {
    return null
  }

  const scrollByCard = (direction: -1 | 1) => {
    const el = scrollerRef.current
    if (!el) return
    const card = el.querySelector<HTMLElement>("[data-rail-card]")
    const amount = card ? card.offsetWidth + 16 : 280
    el.scrollBy({ left: direction * amount, behavior: "smooth" })
  }

  return (
    <section className="border-b border-vx-line bg-vx-bg">
      <div className="vx-section !py-10 small:!py-14">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="vx-section-title">เลือกตามสายงาน / อุปกรณ์</h2>
            <p className="vx-section-sub">
              เลื่อนดูหมวดหลัก — Wi‑Fi สวิตช์ เราเตอร์ กล้อง และเกตเวย์
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="เลื่อนหมวดไปทางซ้าย"
              onClick={() => scrollByCard(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-vx-line bg-vx-surface text-vx-ink transition-colors hover:border-vx-accent hover:text-vx-accent"
            >
              <span aria-hidden className="text-lg leading-none">
                ‹
              </span>
            </button>
            <button
              type="button"
              aria-label="เลื่อนหมวดไปทางขวา"
              onClick={() => scrollByCard(1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-vx-line bg-vx-surface text-vx-ink transition-colors hover:border-vx-accent hover:text-vx-accent"
            >
              <span aria-hidden className="text-lg leading-none">
                ›
              </span>
            </button>
          </div>
        </div>

        <ul
          ref={scrollerRef}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 no-scrollbar"
        >
          {items.map((item) => {
            const src = toDisplayImageUrl(item.imageUrl)
            return (
              <li
                key={item.id}
                data-rail-card
                className="w-[78%] shrink-0 snap-start xsmall:w-[46%] small:w-[31%] medium:w-[23%]"
              >
                <LocalizedClientLink
                  href={item.href}
                  className="group flex h-full min-h-[220px] flex-col overflow-hidden rounded-xl border border-vx-line bg-[#eef4fb] transition-all duration-200 hover:-translate-y-0.5 hover:border-vx-accent/40 hover:shadow-md"
                  data-testid={`home-category-rail-${item.id}`}
                >
                  <div className="px-4 pt-4">
                    <h3 className="text-base font-semibold text-[#1d4f91] small:text-lg">
                      {item.title}
                    </h3>
                  </div>
                  <div className="relative mt-2 flex flex-1 items-center justify-center px-4 pb-5 pt-2">
                    {src ? (
                      <div className="relative h-36 w-full">
                        <Image
                          src={src}
                          alt=""
                          fill
                          sizes="(max-width: 512px) 78vw, (max-width: 1024px) 46vw, 23vw"
                          className="object-contain drop-shadow-md transition-transform duration-200 group-hover:scale-[1.03]"
                        />
                      </div>
                    ) : (
                      <div className="flex h-36 w-full items-center justify-center rounded-lg bg-white/60 text-sm text-vx-muted">
                        ดูหมวด
                      </div>
                    )}
                  </div>
                </LocalizedClientLink>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
